import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ClipboardCheck } from "lucide-react";

export function RecordOutcomeDialog({ predictionId, indicatorName, unit, onRecorded, variant = "full" }) {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  if (!isAdmin) return null;

  const submit = async () => {
    if (value === "" || isNaN(Number(value))) {
      toast.error("Enter a valid actual value");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/outcome", { prediction_id: predictionId, actual_value: Number(value) });
      const o = res.data.outcome;
      toast.success(`Recorded — ${o.direction_correct ? "direction correct ✓" : "direction missed ✗"} · ${o.pct_error}% error`);
      setOpen(false);
      setValue("");
      onRecorded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record outcome");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="record-outcome-button"
          className={variant === "full"
            ? "w-full bg-[#F59E0B] hover:bg-[#d98c08] text-black font-semibold"
            : "h-8 px-3 text-xs bg-white/5 hover:bg-white/10 text-[#F9FAFB] border border-white/10"}
        >
          <ClipboardCheck size={16} className="mr-1.5" /> {variant === "full" ? "Record Actual" : "Record"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-white/10 text-[#F9FAFB]">
        <DialogHeader>
          <DialogTitle className="font-display">Record Actual — {indicatorName}</DialogTitle>
          <DialogDescription className="text-xs text-[#9CA3AF]">
            Log the official released figure. The engine computes forecast error, direction accuracy,
            and updates the model for the next release.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs text-[#9CA3AF]">Actual value {unit ? `(${unit})` : ""}</Label>
          <Input
            data-testid="outcome-value-input"
            type="number"
            step="any"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. 185"
            className="bg-[#0A0D14] border-white/10 font-mono-num"
          />
        </div>
        <DialogFooter>
          <Button data-testid="outcome-submit-button" onClick={submit} disabled={loading}
            className="bg-[#F59E0B] hover:bg-[#d98c08] text-black font-semibold">
            {loading ? "Recording…" : "Submit & Learn"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
